import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, BatteryCharging, Building2, Check, FileCheck, Flame, Home as HomeIcon, Layers, ShieldCheck, Sun, Waves, Wind, Zap } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { FadeUp, KineticLines, Overline, SectionHead, EASE } from "@/components/site/Reveal";
import Marquee from "@/components/site/Marquee";
import { LOGO_SRC } from "@/components/site/Logo";

const HOUSE_IMG = "/api/files/melwest-eco-energy/brand/energy-house.jpeg";

const TRUST = [
  { icon: ShieldCheck, label: "Solar Victoria authorised retailer" },
  { icon: BadgeCheck, label: "Clean Energy Regulator accredited" },
  { icon: FileCheck, label: "VEU aggregator" },
  { icon: Sun, label: "STC certificate creator" },
];

const MARQUEE_ITEMS = [
  "Heat Pumps", "Air Conditioners", "Solar Panels", "Batteries",
  "Hot Water Rebates up to $1,400", "Solar PV Rebates up to $1,400",
  "VEU Discounts", "Federal STCs", "Servicing Greater Melbourne & Regional Victoria",
];

const VALUES = [
  {
    n: "01",
    title: "Accredited & accountable",
    copy: "A Solar Victoria authorised retailer working with Clean Energy Council accredited installers.",
  },
  {
    n: "02",
    title: "Rebates, handled",
    copy: "Every rebate you qualify for is priced into your quote. The discounts come off your invoice — the paperwork comes to us, not you.",
  },
  {
    n: "03",
    title: "Right-sized systems",
    copy: "We design to how your household actually uses energy, then quote a system that fits — no upselling to panels you'll never need.",
  },
  {
    n: "04",
    title: "A local Victorian crew",
    copy: "Based in Victoria, for Victoria. We service Greater Melbourne and regional Victoria.",
  },
];

const STEPS = [
  {
    title: "Run the eligibility check",
    copy: "Six quick questions. We'll tell you which Victorian and federal rebates you're likely to qualify for.",
  },
  {
    title: "Free site inspection",
    copy: "We visit, measure, and talk through the right system for your home — no cost, no obligation.",
  },
  {
    title: "Installed, rebates claimed",
    copy: "Accredited installers fit your system, and every rebate comes straight off your invoice.",
  },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-bone text-forest-800" data-testid="hero-section">
      <div
        className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-sage/80 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-32 left-[-8%] h-[420px] w-[420px] rounded-full bg-[#FDF3E0] blur-3xl"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-6 pb-24 pt-32 sm:pt-36 lg:grid-cols-12 lg:gap-8 lg:pb-28 lg:pt-40">
        <div className="lg:col-span-6">
          <h1 className="font-display mt-0 text-[clamp(2.7rem,6.5vw,4.9rem)] font-extrabold leading-[1.0] tracking-tight text-forest-700">
            <KineticLines
              lines={[
                "Warm in winter.",
                "Cool in summer.",
                <span key="sun" className="italic text-terra">Powered by the sun.</span>,
              ]}
              delay={0.15}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-lg text-base leading-relaxed text-moss sm:text-lg"
          >
            Heat pumps, air conditioning, solar and batteries for Victorian
            homes — with every rebate you're owed, priced into your quote.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-quote"
              className="inline-flex items-center gap-2 rounded-full bg-terra px-7 py-4 text-sm font-bold text-forest-900 transition-colors hover:bg-terra-dark"
            >
              Get a free quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/rebates"
              data-testid="hero-cta-eligibility"
              className="inline-flex items-center gap-2 rounded-full bg-forest-700 px-7 py-4 text-sm font-bold text-bone transition-colors hover:bg-forest-600"
            >
              Check rebate eligibility
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-3"
          >
            {TRUST.map(({ icon: Icon, label }, i) => (
              <span key={label} className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-moss" data-testid="hero-trust-badge">
                <Icon className={`h-4 w-4 ${["text-leaf", "text-wave", "text-terra"][i % 3]}`} /> {label}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-6">
          <motion.div style={{ y }} className="relative mx-auto max-w-[520px]" data-testid="hero-house-frame">
            <motion.div
              className="absolute left-1/2 top-1/2 -z-0 h-[115%] w-[115%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-sage to-[#FDF3E0]"
              animate={{ rotate: 360 }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
              aria-hidden="true"
            />
            <motion.div
              className="relative z-10 overflow-hidden rounded-2xl border border-forest-700/10 shadow-xl shadow-forest-700/10"
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <img
                src={HOUSE_IMG}
                alt="A fully electric Victorian home with solar, battery, heat pump and air conditioning"
                className="w-full"
              />
            </motion.div>
            {/* energy pulses from roof to battery */}
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className={`absolute z-20 h-3 w-3 rounded-full ${["bg-terra", "bg-wave", "bg-leaf"][i]} shadow`}
                style={{ left: "56%", top: "16%" }}
                animate={{ x: [0, 34, 62, 48, 0], y: [0, 44, 96, 138, 0], opacity: [0, 1, 1, 0.5, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: i * 1, ease: "linear" }}
                aria-hidden="true"
              />
            ))}
            {/* product chips */}
            {[
              { icon: Sun, label: "Solar panels", pos: "left-[-4%] top-[6%]", c: "text-terra" },
              { icon: Flame, label: "Heat pumps", pos: "left-[-8%] bottom-[18%]", c: "text-leaf" },
              { icon: BatteryCharging, label: "Batteries", pos: "right-[-4%] top-[30%]", c: "text-wave" },
              { icon: Wind, label: "Air con", pos: "right-[-6%] bottom-[4%]", c: "text-forest-700" },
            ].map(({ icon: Icon, label, pos, c }, i) => (
              <motion.span
                key={label}
                className={`absolute z-20 inline-flex items-center gap-1.5 rounded-full border border-forest-700/10 bg-white/95 px-3.5 py-2 text-[11px] font-bold text-forest-700 shadow-md ${pos}`}
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3.4 + i * 0.4, repeat: Infinity, delay: i * 0.5, ease: "easeInOut" }}
                data-testid={`hero-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className={`h-3.5 w-3.5 ${c}`} /> {label}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function EnergyFlow() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [24, -24]);

  const FLOW = [
    { icon: Sun, label: "Solar captures", color: "text-terra", dot: "bg-terra" },
    { icon: BatteryCharging, label: "Battery stores", color: "text-wave", dot: "bg-wave" },
    { icon: Flame, label: "Heat pump warms", color: "text-leaf", dot: "bg-leaf" },
    { icon: Wind, label: "Air con cools", color: "text-wave", dot: "bg-wave" },
    { icon: HomeIcon, label: "Home runs happy", color: "text-forest-700", dot: "bg-leaf" },
  ];

  return (
    <section ref={ref} className="overflow-hidden bg-bone py-24 md:py-32" data-testid="energy-flow">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHead
          overline="How the energy flows"
          title="One sunny roof. Four clever systems. Zero gas bills."
          copy="Here's the whole picture: solar panels feed the battery, the battery drives your heat pump hot water and air conditioning, and the whole home runs on your own sunshine."
        />
        <div className="mt-14 grid items-center gap-12 lg:grid-cols-12">
          <motion.div style={{ y }} className="relative lg:col-span-5" data-testid="energy-house-frame">
            <div className="absolute -inset-6 rounded-full bg-sage/70 blur-2xl" aria-hidden="true" />
            <motion.div
              className="relative z-10 mx-auto w-full max-w-md overflow-hidden rounded-2xl border border-forest-700/10 shadow-lg shadow-forest-700/5"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <img
                src={HOUSE_IMG}
                alt="Illustration of a fully electric home with solar, battery, heat pump and air conditioning"
                className="w-full"
              />
            </motion.div>
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className={`absolute z-20 h-3 w-3 rounded-full ${["bg-terra", "bg-wave", "bg-leaf"][i]}`}
                style={{ left: "58%", top: "18%" }}
                animate={{ x: [0, 30, 60, 45, 0], y: [0, 40, 90, 130, 0], opacity: [0, 1, 1, 0.6, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: i * 1, ease: "linear" }}
                aria-hidden="true"
              />
            ))}
          </motion.div>

          <div className="lg:col-span-7">
            <div className="space-y-4">
              {FLOW.map((step, i) => (
                <FadeUp key={step.label} delay={i * 0.08}>
                  <div
                    className="flex items-center gap-5 rounded-xl border border-forest-700/10 bg-white p-5 transition-shadow hover:shadow-md"
                    data-testid={`energy-flow-step-${i}`}
                  >
                    <span className={`relative inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-bone-dark`}>
                      <step.icon className={`h-5 w-5 ${step.color}`} />
                      <motion.span
                        className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ${step.dot}`}
                        animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                        transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.35, ease: "easeInOut" }}
                      />
                    </span>
                    <div>
                      <p className="font-display text-lg font-bold text-forest-700">{step.label}</p>
                      <p className="mt-0.5 text-sm text-moss">
                        {["Panels on your roof turn sunlight into power from first light.",
                          "Excess sunshine parks itself in your battery for the evening peak.",
                          "Hot water heated by air, not gas — rebates applied up front.",
                          "Quiet, precise cooling for those 38°C February days.",
                          "Everything together: a home that barely notices bill season."][i]}
                      </p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.2}>
              <Link
                to="/book-inspection"
                data-testid="energy-flow-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-terra px-7 py-4 text-sm font-bold text-forest-900 transition-colors hover:bg-terra-dark"
              >
                See it on your roof — book a free inspection <ArrowRight className="h-4 w-4" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryBento({ categories }) {
  const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];
  const heights = ["h-[380px] md:h-[460px]", "h-[380px] md:h-[460px]", "h-[380px] md:h-[460px]", "h-[380px] md:h-[460px]"];
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="home-categories">
      <SectionHead
        overline="What we install"
        title="Four ways to make your home cheap to run"
        copy="Every system we quote is eligible-system, accredited-installer, rebate-applied. Pick your starting point."
      />
      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-12">
        {categories.map((cat, i) => (
          <FadeUp key={cat.slug} delay={i * 0.07} className={spans[i % 4]}>
            <Link
              to={`/products/${cat.slug}`}
              data-testid={`category-card-${cat.slug}`}
              className={`clip-notch group relative block w-full overflow-hidden ${heights[i % 4]}`}
            >
              {cat.image ? (
                <img
                  src={cat.image}
                  alt={cat.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-[1.05]"
                />
              ) : (
                <div className="absolute inset-0 bg-sage" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-forest-900/85 via-forest-900/30 to-transparent" />
              <div className="absolute bottom-0 left-0 p-7 sm:p-8">
                <Overline className="text-terra">{cat.overline}</Overline>
                <h3 className="font-display mt-2 text-2xl font-extrabold text-bone sm:text-3xl">{cat.name}</h3>
                <p className="mt-1.5 max-w-sm text-sm text-bone/75">{cat.tagline}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-bone transition-colors group-hover:text-terra">
                  Explore
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-terra text-bone transition-transform group-hover:translate-x-1">
                    <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </span>
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

const UPCOMING_REBATES = [
  {
    icon: Layers,
    color: "text-terra",
    title: "Ceiling insulation",
    copy: "Draughty Victorian homes lose their heat straight through the roof. Insulation upgrades are on the VEU horizon — register now and we'll call you first.",
  },
  {
    icon: Waves,
    color: "text-wave",
    title: "Pool pumps",
    copy: "Variable-speed pool pumps are entering the VEU scheme — a quiet, efficient pump that pays its way for backyard pools.",
  },
  {
    icon: Building2,
    color: "text-leaf",
    title: "Commercial solar",
    copy: "Solar for businesses with the federal STC discount applied upfront — with more commercial programs brewing for Victorian sites.",
  },
  {
    icon: BatteryCharging,
    color: "text-terra",
    title: "Commercial battery",
    copy: "Battery storage sized for shops, warehouses and offices — on the way as Victorian programs expand.",
  },
];

function UpcomingRebates() {
  const [email, setEmail] = useState("");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | done
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (saveState === "saving") return;
    setError("");
    setSaveState("saving");
    try {
      await api.post("/leads/subscribe", { email, topic: "Upcoming rebates" });
      setSaveState("done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setSaveState("idle");
    }
  };

  return (
    <section className="bg-bone-dark py-24 md:py-32" data-testid="upcoming-rebates">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHead
          overline="On the horizon"
          title="More rebates are coming to Victoria"
          copy="The VEU scheme and government programs keep expanding. Here's the next wave we're watching — register your interest and we'll call you the moment they land."
        />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {UPCOMING_REBATES.map((r, i) => (
            <FadeUp key={r.title} delay={i * 0.07} className="h-full">
              <div
                className="flex h-full flex-col rounded-xl border border-forest-700/10 bg-white p-6 transition-shadow hover:shadow-md"
                data-testid={`upcoming-card-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-bone-dark">
                    <r.icon className={`h-5 w-5 ${r.color}`} />
                  </span>
                  <span className="rounded-full border border-terra/40 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-terra-dark">
                    In the pipeline
                  </span>
                </div>
                <h3 className="font-display mt-4 text-lg font-bold text-forest-700">{r.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-moss">{r.copy}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.15}>
          <div className="mt-10 rounded-xl border border-forest-700/10 bg-white p-7">
            {saveState === "done" ? (
              <p className="flex items-center gap-2 text-sm font-bold text-forest-700" data-testid="signup-success">
                <Check className="h-4 w-4 text-leaf" />
                You're on the list — we'll be in touch the moment the program opens.
              </p>
            ) : (
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <p className="max-w-xl text-sm text-moss">
                  Programs, discounts and timing are set by the Victorian
                  Government and program administrators — we update this page as
                  each one is confirmed.
                </p>
                <form onSubmit={submit} noValidate className="flex w-full flex-col gap-2 lg:w-auto lg:flex-row lg:items-start">
                  <div className="flex w-full flex-col gap-2 lg:w-auto">
                    <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com.au"
                        data-testid="signup-email-input"
                        className="w-full rounded-full border border-forest-700/20 bg-bone px-4 py-3 text-sm text-forest-900 placeholder:text-moss/50 focus:border-leaf focus:outline-none sm:w-64"
                        aria-label="Your email address"
                      />
                      <button
                        type="submit"
                        disabled={saveState === "saving"}
                        data-testid="signup-submit"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-terra px-6 py-3 text-sm font-bold text-forest-900 transition-colors hover:bg-terra-dark disabled:opacity-60"
                      >
                        {saveState === "saving" ? "Registering…" : "Register your interest"}
                        {saveState === "saving" ? null : <ArrowRight className="h-4 w-4" />}
                      </button>
                    </div>
                    {error && (
                      <p className="text-xs font-bold text-red-500" data-testid="signup-error">{error}</p>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function RebateBand({ rebates }) {
  const list = (rebates && rebates.length ? rebates : [
    { title: "Solar Homes Hot Water Rebate", amount_label: "Up to $1,000" },
    { title: "Solar PV Rebate", amount_label: "Up to $1,400" },
    { title: "VEU Hot Water Discounts", amount_label: "Varies by system" },
    { title: "Federal STC Incentive", amount_label: "Upfront discount" },
  ]).slice(0, 4);
  return (
    <section className="bg-[#FBF1DD]" data-testid="home-rebate-band">
      <div className="relative z-10 mx-auto grid max-w-7xl gap-12 px-6 py-20 md:py-28 lg:grid-cols-2 lg:items-center">
        <div>
          <FadeUp>
            <Overline className="text-terra-dark">Victorian &amp; federal incentives</Overline>
            <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-forest-700 sm:text-4xl lg:text-[2.75rem] lg:leading-[1.08]">
              The rebates you're owed, priced into your quote.
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-moss">
              As a Solar Victoria authorised retailer, we apply every rebate you
              qualify for directly to your invoice — including Solar Homes hot
              water and solar PV rebates, VEU discounts and federal STCs.
            </p>
            <Link
              to="/rebates"
              data-testid="home-rebate-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-forest-700 px-7 py-4 text-sm font-bold text-bone transition-transform hover:-translate-y-0.5"
            >
              Check your eligibility in 60 seconds <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {list.map((r, i) => (
            <FadeUp key={r.title || i} delay={i * 0.08}>
              <div
                className="h-full rounded-lg border border-terra/30 bg-white p-6 shadow-sm transition-transform hover:-translate-y-0.5"
                data-testid={`home-rebate-chip-${i}`}
              >
                <p className="font-display text-2xl font-extrabold text-terra-dark">{r.amount_label}</p>
                <p className="mt-2 text-sm font-semibold text-forest-700">{r.title}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [rebates, setRebates] = useState(null);
  const [categories, setCategories] = useState([
    { slug: "heat-pumps", name: "Heat Pumps", overline: "Hot water heat pumps", tagline: "Heat your water with air, not gas.", image: "" },
    { slug: "air-conditioners", name: "Air Conditioners", overline: "Heating & cooling", tagline: "One system for four seasons in a day.", image: "" },
    { slug: "solar-panels", name: "Solar Panels", overline: "Solar PV systems", tagline: "Your roof is an asset. Use it.", image: "" },
    { slug: "batteries", name: "Batteries", overline: "Battery storage", tagline: "Store your sunshine for after dark.", image: "" },
  ]);

  useEffect(() => {
    api.get("/rebates").then((d) => setRebates(d.data)).catch(() => {});
    api.get("/categories").then((d) => setCategories(d.data)).catch(() => {});
  }, []);

  return (
    <>
      <Hero />
      <Marquee
        items={MARQUEE_ITEMS}
        className="border-y border-forest-700/15 bg-bone-dark py-5 font-display text-lg font-bold uppercase tracking-[0.14em] text-forest-700"
      />
      <CategoryBento categories={categories} />
      <EnergyFlow />
      <section className="bg-bone-dark py-24 md:py-32" data-testid="home-values">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHead overline="Why Melwest" title="A retailer that treats rebates as the baseline" />
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <FadeUp key={v.n} delay={i * 0.08} className="border-t-2 border-forest-700/15 pt-6">
                <p className={`font-display text-4xl font-extrabold ${["text-leaf", "text-wave", "text-terra", "text-leaf"][i]}`}>{v.n}</p>
                <h3 className="font-display mt-4 text-lg font-bold text-forest-700">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-moss">{v.copy}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
      <RebateBand rebates={rebates} />
      <UpcomingRebates />
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="home-steps">
        <SectionHead
          overline="How it works"
          title="From 'what's a heat pump' to installed — in three moves"
        />
        <div className="mt-14 grid gap-10 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <FadeUp key={s.title} delay={i * 0.1}>
              <div className="flex items-start gap-5">
                <span className="font-display text-5xl font-extrabold text-forest-100 [-webkit-text-stroke:2px_#1D4A12]">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold text-forest-700">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-moss">{s.copy}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>
      <section className="grain bg-forest-800 text-bone" data-testid="home-final-cta">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-10 right-10 z-0 hidden rounded-2xl border border-bone/10 bg-bone/5 p-5 lg:block"
        >
          <img src={LOGO_SRC} alt="" className="h-28 opacity-25" />
        </div>
        <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-6 py-24 text-center md:py-32">
          <FadeUp>
            <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
              Ready to stop renting your energy?
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-bone/75">
              Tell us about your home and we'll come back with a tailored quote
              — every rebate you qualify for already priced in.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <Link to="/contact" data-testid="home-final-quote" className="inline-flex items-center gap-2 rounded-full bg-terra px-7 py-4 text-sm font-bold text-bone transition-colors hover:bg-terra-dark">
                Get a free quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/book-inspection" data-testid="home-final-inspection" className="inline-flex items-center gap-2 rounded-full border border-bone/30 px-7 py-4 text-sm font-bold text-bone transition-colors hover:border-terra hover:text-terra">
                Book a site inspection
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
